/*
=============================================================================
FILE: varient-gallery.js
PURPOSE:
  Custom Web Component for variant carousel slider and PD measurement guide modal
  Provides interactive slider for options with >3 variants and modal system for
  displaying prescription measurement guides

LOCATION: Shopify product page (injected by enhanced-vaient-picker.liquid)
CUSTOM ELEMENT: <variant-gallery>

COMPONENTS:
  1. SLIDER SYSTEM (180px cards, 16px gap, 3 visible cards)
     - Momentum-based drag with physics simulation
     - Touch and mouse support
     - Left/Right navigation buttons
     - Automatic pagination state management

  2. KEYBOARD NAVIGATION
     - Arrow Left/Right: navigate carousel
     - Escape: close modal
     - Respects accessibility standards (tabindex)

  3. PD GUIDE MODAL SYSTEM
     - Pattern-based detection of prescription guide links
     - Retry mechanism (500ms, 1500ms, 3000ms)
     - Body-level modal creation and injection
     - Window API: window.openPDGuideModal(), window.closePDGuideModal()

  4. TOUCH & DRAG SUPPORT
     - Touch events (mobile)
     - Mouse drag (desktop)
     - Momentum physics (30% elastic boundaries)
     - Drag threshold: 5px before activating slide
     - Velocity detection for momentum calculation

KEY FEATURES:
  - CustomElementAPI (native Web Component)
  - No external framework dependencies
  - Passive event listeners for performance
  - Debounced resize handling
  - Memory-efficient card caching
  - Configurable dimensions (card width, gap, visible cards)

CONFIGURATION:
  - CARD_WIDTH: 180px (individual card width)
  - GAP: 16px (spacing between cards)
  - VISIBLE_CARDS: 3 (cards visible at once)
  - DRAG_THRESHOLD: 5px (minimum drag distance)
  - MOMENTUM_FRICTION: 0.95 (velocity decay)
  - MAX_VELOCITY: 80 (maximum px/frame)

DEPENDENCIES:
  - enhanced-vaient-picker.liquid (loads this script)
  - custom.css (styles for cards and modal)
  - custom.js (companion JS system)
  - No external libraries required

=============================================================================
*/

/* Enhanced Variant Gallery - Optimized Slider + PD Guide System */

if (!customElements.get('variant-gallery')) {
  customElements.define(
    'variant-gallery',
    class VariantGallery extends HTMLElement {
      constructor() {
        super();
        // ===== SLIDER CONFIGURATION =====
        this.CARD_WIDTH = 180;          // Individual card width in pixels
        this.GAP = 16;                  // Spacing between cards
        this.VISIBLE_CARDS = 3;         // Cards visible in viewport at once
        this.DRAG_THRESHOLD = 5;        // Minimum drag distance to activate slide
        this.currentSlide = 0;          // Current slide index
        this.isSliding = false;         // Animation in progress flag
        this.hasDragged = false;        // Drag gesture detected flag
        this.cachedElements = {};       // DOM element cache for performance
      }

      connectedCallback() {
        this.setupSlider();
      }

      debounce(func, wait) {
        let timeout;
        return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => func.apply(this, args), wait);
        };
      }

      // ===== SLIDER SETUP & INITIALIZATION =====
      // Initialize all slider DOM elements, dimensions, and event listeners
      setupSlider() {
        this.cachedElements = {
          container: this.querySelector('.variant-slider-container'),
          track: this.querySelector('.variant-slider-track'),
          viewport: this.querySelector('.variant-slider-viewport'),
          prevBtn: this.querySelector('.variant-slider-prev'),
          nextBtn: this.querySelector('.variant-slider-next'),
          cards: this.querySelectorAll('.product-variant-card')
        };
        
        const { container, track, viewport, prevBtn, nextBtn, cards } = this.cachedElements;
        if (!container || !track || !viewport || !prevBtn || !nextBtn || !cards.length) return;

        // Calculate total widths for viewport and track
        const totalWidth = (this.CARD_WIDTH * this.VISIBLE_CARDS) + (this.GAP * (this.VISIBLE_CARDS - 1));
        const trackWidth = (this.CARD_WIDTH * cards.length) + (this.GAP * (cards.length - 1));
        
        viewport.style.width = `${totalWidth}px`;
        viewport.style.maxWidth = `${totalWidth}px`;
        track.style.width = `${trackWidth}px`;
        
        // Size each card consistently
        cards.forEach(card => {
          card.style.width = `${this.CARD_WIDTH}px`;
          card.style.minWidth = `${this.CARD_WIDTH}px`;
          card.style.flexShrink = '0';
        });

        // Calculate maximum slide position
        this.maxSlides = Math.max(0, cards.length - this.VISIBLE_CARDS);
        this.currentSlide = 0;

        // Clone button nodes to remove old event listeners
        const newPrevBtn = prevBtn.cloneNode(true);
        const newNextBtn = nextBtn.cloneNode(true);
        prevBtn.parentNode.replaceChild(newPrevBtn, prevBtn);
        nextBtn.parentNode.replaceChild(newNextBtn, nextBtn);
        
        this.cachedElements.prevBtn = newPrevBtn;
        this.cachedElements.nextBtn = newNextBtn;

        // Attach navigation button listeners
        newPrevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (!this.isSliding && this.currentSlide > 0) this.slidePrev();
        });

        newNextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (!this.isSliding && this.currentSlide < this.maxSlides) this.slideNext();
        });

        this.addTouchSupport(track);
        this.updateNavigationState();
      }

      // ===== NAVIGATION: PREVIOUS SLIDE =====
      // Move carousel one slide to the left
      slidePrev() {
        if (this.isSliding || this.currentSlide <= 0) return;
        this.currentSlide = Math.max(0, this.currentSlide - 1);
        this.updateSlidePosition();
        this.updateNavigationState();
      }

      // ===== NAVIGATION: NEXT SLIDE =====
      // Move carousel one slide to the right
      slideNext() {
        if (this.isSliding || this.currentSlide >= this.maxSlides) return;
        this.currentSlide = Math.min(this.maxSlides, this.currentSlide + 1);
        this.updateSlidePosition();
        this.updateNavigationState();
      }

      // ===== SLIDE POSITION UPDATE =====
      // Translate track to show current slide with optional smooth animation
      updateSlidePosition(smooth = true) {
        const track = this.cachedElements.track;
        if (!track) return;

        this.isSliding = smooth;
        const translateX = this.currentSlide * (this.CARD_WIDTH + this.GAP);
        
        track.style.transition = smooth ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
        track.style.transform = `translateX(-${translateX}px)`;
        
        if (smooth) setTimeout(() => this.isSliding = false, 400);
      }

      setTrackPosition(translateX, smooth = false) {
        const track = this.cachedElements.track;
        if (!track) return;
        
        track.style.transition = smooth ? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
        track.style.transform = `translateX(${translateX}px)`;
      }

      updateNavigationState() {
        const { prevBtn, nextBtn } = this.cachedElements;
        if (!prevBtn || !nextBtn) return;

        const atStart = this.currentSlide <= 0;
        const atEnd = this.currentSlide >= this.maxSlides;
        
        prevBtn.classList.toggle('disabled', atStart);
        prevBtn.disabled = atStart;
        nextBtn.classList.toggle('disabled', atEnd);
        nextBtn.disabled = atEnd;
      }

      // ===== TOUCH & DRAG SUPPORT (MOBILE & DESKTOP) =====
      // Implements drag gesture with momentum physics and elastic boundaries
      // Touch: mobile swipe, Mouse: click and drag
      // Drag threshold: 5px minimum to activate; velocity detection for momentum
      addTouchSupport(track) {
        let startX, currentX, isDragging, startTranslateX, currentTranslateX, velocityX, lastX, lastTime = 0;

        // Calculate boundaries for track movement (elastic constraints)
        const getTrackBounds = () => {
          const { viewport, cards } = this.cachedElements;
          const viewportWidth = viewport ? viewport.offsetWidth : this.CARD_WIDTH * 3 + this.GAP * 2;
          const totalTrackWidth = (this.CARD_WIDTH * cards.length) + (this.GAP * (cards.length - 1));
          const maxTranslateX = Math.max(0, totalTrackWidth - viewportWidth);
          return { min: -maxTranslateX, max: 0, trackWidth: totalTrackWidth, viewportWidth };
        };

        // Initialize drag gesture (mousedown or touchstart)
        const startDrag = (clientX) => {
          if (this.isSliding) return;
          startX = currentX = lastX = clientX;
          lastTime = Date.now();
          isDragging = true;
          this.hasDragged = false;
          velocityX = 0;
          const matrix = new DOMMatrix(window.getComputedStyle(track).transform);
          startTranslateX = currentTranslateX = matrix.m41;
        };

        // Update drag position (mousemove or touchmove); apply elastic boundaries at edges
        const updateDrag = (clientX) => {
          if (!isDragging) return;
          currentX = clientX;
          const deltaX = currentX - startX;
          
          // Activate drag only after exceeding threshold (5px)
          if (!this.hasDragged && Math.abs(deltaX) > this.DRAG_THRESHOLD) {
            this.hasDragged = true;
            track.style.transition = 'none';
            track.style.cursor = 'grabbing';
            track.classList.add('is-dragging');
          }
          
          // Calculate new position with elastic boundary effect (30% resistance)
          if (this.hasDragged) {
            const bounds = getTrackBounds();
            let newTranslateX = startTranslateX + deltaX;
            
            // Apply elastic effect at boundaries (30% friction)
            if (newTranslateX > bounds.max) {
              newTranslateX = bounds.max + ((newTranslateX - bounds.max) * 0.3);
            } else if (newTranslateX < bounds.min) {
              newTranslateX = bounds.min - ((bounds.min - newTranslateX) * 0.3);
            }
            
            currentTranslateX = newTranslateX;
            const currentTime = Date.now();
            const timeDelta = currentTime - lastTime;
            if (timeDelta > 0) {
              velocityX = (currentX - lastX) / timeDelta;
              lastX = currentX;
              lastTime = currentTime;
            }
            this.setTrackPosition(currentTranslateX, false);
          }
        };

        const endDrag = () => {
          if (!isDragging) return;
          isDragging = false;
          track.style.cursor = 'grab';
          track.classList.remove('is-dragging');
          
          if (this.hasDragged) {
            const bounds = getTrackBounds();
            let finalTranslateX = currentTranslateX;
            
            if (Math.abs(velocityX) > 0.1) finalTranslateX += velocityX * 150;
            finalTranslateX = Math.max(bounds.min, Math.min(bounds.max, finalTranslateX));
            
            const cardStep = this.CARD_WIDTH + this.GAP;
            const targetSlide = Math.round(-finalTranslateX / cardStep);
            this.currentSlide = Math.max(0, Math.min(this.maxSlides, targetSlide));
            
            this.setTrackPosition(-targetSlide * cardStep, true);
            this.updateNavigationState();
          }
          setTimeout(() => this.hasDragged = false, 50);
        };

        track.addEventListener('touchstart', (e) => startDrag(e.touches[0].clientX), { passive: true });
        track.addEventListener('touchmove', (e) => { updateDrag(e.touches[0].clientX); if (this.hasDragged) e.preventDefault(); }, { passive: false });
        track.addEventListener('touchend', (e) => { endDrag(); if (this.hasDragged) e.preventDefault(); }, { passive: false });

        track.addEventListener('mousedown', (e) => {
          startDrag(e.clientX);
          const moveHandler = (e) => { updateDrag(e.clientX); if (this.hasDragged) e.preventDefault(); };
          const upHandler = (e) => {
            document.removeEventListener('mousemove', moveHandler);
            document.removeEventListener('mouseup', upHandler);
            if (this.hasDragged) e.preventDefault();
            endDrag();
          };
          document.addEventListener('mousemove', moveHandler);
          document.addEventListener('mouseup', upHandler);
        });

        Object.assign(track.style, { userSelect: 'none', cursor: 'grab' });
        track.addEventListener('contextmenu', (e) => { if (isDragging) e.preventDefault(); });
        track.addEventListener('click', (e) => { if (this.hasDragged) { e.preventDefault(); e.stopPropagation(); return false; } }, true);

        this.addEventListener('keydown', (e) => {
          if (this.isSliding) return;
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.slidePrev(); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); this.slideNext(); }
        });
        this.setAttribute('tabindex', '0');
      }

      handleVariantChange(selectedCard) {
        this.cachedElements.cards.forEach(card => card.classList.remove('selected'));
        selectedCard.classList.add('selected');
        
        const cardIndex = Array.from(this.cachedElements.cards).indexOf(selectedCard);
        if (cardIndex >= 0) {
          const targetSlide = Math.max(0, Math.min(this.maxSlides, cardIndex - 1));
          if (targetSlide !== this.currentSlide) {
            this.currentSlide = targetSlide;
            this.updateSlidePosition();
            this.updateNavigationState();
          }
        }
      }
    }
  );
}

// Auto-initialize variant galleries
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('variant-gallery').forEach(gallery => {
    if (!gallery.initialized) {
      gallery.initialized = true;
    }
  });
});
 
/*
=============================================================================
SYSTEM: PD GUIDE MODAL & LINK INJECTION
=============================================================================
Pattern-based detection of Pupil Distance (PD) fields and injection of 
measurement guide link. Creates modal system for displaying guides.
Links are automatically injected; modals created globally by this system.
=============================================================================
*/

/**
 * ===== PD GUIDE LINK INJECTOR =====
 * This file injects "Guide to measure PD" link into the form
 * Pattern-based detection: searches label text for "PD", "Pupil Distance", etc.
 * The modal system opens the guide when link is clicked (separate module)
 */

(function() {
  'use strict';

  const CONFIG = {
    linkText: 'Guide to measure PD (Pupil Distance)',  // Text displayed as link
    linkClass: 'pd-guide-link',
    // Label text patterns for detecting PD field (string literal and regex)
    labelPatterns: [
      'PD (Pupil Distance)',
      'PD (pupil distance)',
      'Pupil Distance',
      'pupil distance',
      'PD Distance',
      /PD\s*\(/i,  // Matches "PD (" or "PD(" case-insensitive
      /pupil\s+distance/i  // Matches "pupil distance" case-insensitive
    ]
  };

  /**
   * Find PD input field by searching labels for pattern matches
   * Uses multiple strategies: 'for' attribute, parent traversal, next sibling
   */
  function findPDInputField() {
    // Get all labels in the form
    const labels = document.querySelectorAll('.mw-label, label, .mw-text--style-strong');
    
    for (const label of labels) {
      const labelText = label.textContent.trim();
      
      // Check if label matches any of our patterns
      const matchesPattern = CONFIG.labelPatterns.some(pattern => {
        if (typeof pattern === 'string') {
          return labelText.includes(pattern);
        } else if (pattern instanceof RegExp) {
          return pattern.test(labelText);
        }
        return false;
      });
      
      if (matchesPattern) {
        console.log('Found PD label:', labelText);
        
        // Try to find the associated input field
        // Method 1: Look for 'for' attribute (standard HTML)
        const forAttr = label.getAttribute('for');
        if (forAttr) {
          const input = document.getElementById(forAttr);
          if (input) {
            console.log('Found PD input via for attribute:', forAttr);
            return input;
          }
        }
        
        // Method 2: Look for input in same parent container
        const container = label.closest('.mw-option, .mw-stack, .form-field');
        if (container) {
          const input = container.querySelector('input[type="text"], input[type="number"]');
          if (input) {
            console.log('Found PD input in same container');
            return input;
          }
        }
        
        // Method 3: Look for next input sibling
        let nextElement = label.parentElement;
        while (nextElement && nextElement.nextElementSibling) {
          nextElement = nextElement.nextElementSibling;
          const input = nextElement.querySelector('input[type="text"], input[type="number"]');
          if (input) {
            console.log('Found PD input as next sibling');
            return input;
          }
        }
      }
    }
    
    console.log('PD input field not found by label');
    return null;
  }

  /**
   * Create and inject the PD guide link after the PD input field
   */
  function injectPDGuideLink() {
    // Try to find the PD input field by label
    const pdInput = findPDInputField();
    
    if (!pdInput) {
      console.log('PD input field not found, retrying...');
      return false;
    }

    // Check if link already exists
    if (document.querySelector(`.${CONFIG.linkClass}`)) {
      console.log('PD guide link already exists');
      return true;
    }

    // Find the container to insert the link
    let container = pdInput.closest('.mw-option__container');
    if (!container) {
      container = pdInput.closest('.mw-stack__item');
    }
    
    if (!container) {
      console.log('Could not find suitable container for PD guide link');
      return false;
    }

    const linkWrapper = document.createElement('div');
    linkWrapper.className = 'mw-stack__item pd-guide-link-wrapper';
    linkWrapper.style.marginTop = '8px';

    const link = document.createElement('a');
    link.href = 'javascript:void(0);';
    link.className = CONFIG.linkClass;
    link.textContent = CONFIG.linkText;
    link.addEventListener('click', (e) => {
      e.preventDefault();
      if (typeof window.openPDGuideModal === 'function') window.openPDGuideModal();
      else console.error('openPDGuideModal function not found');
    });

    linkWrapper.appendChild(link);

    // Insert the link after the container
    const parentStack = container.closest('.mw-stack');
    if (parentStack) {
      // Find the last stack item and insert after it
      const stackItems = parentStack.querySelectorAll('.mw-stack__item');
      const lastItem = stackItems[stackItems.length - 1];
      if (lastItem) {
        lastItem.parentNode.insertBefore(linkWrapper, lastItem.nextSibling);
      } else {
        parentStack.appendChild(linkWrapper);
      }
    } else {
      // Fallback: insert after the input's parent
      container.parentNode.insertBefore(linkWrapper, container.nextSibling);
    }

    console.log('✅ PD guide link injected successfully');
    return true;
  }

  function init() {
    if (!injectPDGuideLink()) {
      [500, 1500, 3000].forEach(delay => setTimeout(injectPDGuideLink, delay));
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  setTimeout(init, 1000);
  setTimeout(init, 2000);
})();



/**
 * PD Guide Modal - Global Modal System
 * This file creates and manages the modal popup (should be loaded globally on product pages)
 * Separate from the link injection to avoid modal being inside custom blocks
 */

(function() {
  'use strict';

  const CONFIG = {
    modalId: 'pd-guide-modal',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0699/3539/9095/files/measuring_pd.webp' // Update with your actual image URL
  };

  /**
   * Create and inject the modal HTML at the body level (not inside any custom block)
   * CRITICAL: Uses setTimeout to ensure modal is added AFTER all page content loads
   */
  function createModal() {
    if (document.getElementById(CONFIG.modalId)) return;

    setTimeout(() => {
      if (document.getElementById(CONFIG.modalId)) return;

      const modal = document.createElement('div');
      Object.assign(modal, {id: CONFIG.modalId, className: 'pd-modal'});
      modal.style.display = 'none';

      const overlay = document.createElement('div');
      overlay.className = 'pd-modal-overlay';

      const content = document.createElement('div');
      content.className = 'pd-modal-content';

      const closeBtn = document.createElement('button');
      closeBtn.className = 'pd-modal-close';
      closeBtn.setAttribute('aria-label', 'Close modal');
      closeBtn.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      const imageWrapper = document.createElement('div');
      imageWrapper.className = 'pd-modal-image-wrapper';

      const img = document.createElement('img');
      Object.assign(img, {src: CONFIG.imageUrl, alt: 'Guide to measure PD (Pupil Distance)', loading: 'lazy'});

      imageWrapper.appendChild(img);
      content.append(closeBtn, imageWrapper);
      modal.append(overlay, content);

      if (document.body) document.body.appendChild(modal);
      setupModalEventListeners();
    }, 0);
  }

  function setupModalEventListeners() {
    const modal = document.getElementById(CONFIG.modalId);
    if (!modal) return;

    const elements = {
      overlay: modal.querySelector('.pd-modal-overlay'),
      closeBtn: modal.querySelector('.pd-modal-close'),
      content: modal.querySelector('.pd-modal-content')
    };

    if (elements.closeBtn) {
      elements.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closePDGuideModal();
      });
    }

    if (elements.overlay) {
      elements.overlay.addEventListener('click', (e) => {
        e.preventDefault();
        closePDGuideModal();
      });
    }

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closePDGuideModal();
    });

    if (elements.content) {
      elements.content.addEventListener('click', (e) => e.stopPropagation());
    }
  }

  window.openPDGuideModal = function() {
    const modal = document.getElementById(CONFIG.modalId);
    if (modal) {
      modal.style.display = 'flex';
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    } else {
      createModal();
      setTimeout(window.openPDGuideModal, 100);
    }
  };

  window.closePDGuideModal = function() {
    const modal = document.getElementById(CONFIG.modalId);
    if (modal) {
      modal.style.display = 'none';
      modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  };

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' || e.keyCode === 27) closePDGuideModal();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', createModal);
  else createModal();

})();
